/**
 * Unified task service for managing tasks from different sources
 * Handles conversion, storage, and synchronization
 */

import path from 'path';
import fs from 'fs-extra';
import yaml from 'js-yaml';
import { UnifiedTask, AdapterFactory } from './base-adapter.js';
import { GongfuAdapter } from './gongfu-adapter.js';
import { WorkflowAdapter } from './workflow-adapter.js';
import type { Task } from '../types/index.js';
import type { GongfuTask } from './gongfu-adapter.js';

export interface UnifiedTaskServiceOptions {
  workflowDir?: string;
  gongfuDir?: string;
  autoSync?: boolean;
}

export class UnifiedTaskService {
  private workflowDir: string;
  private gongfuDir: string;
  private autoSync: boolean;
  
  constructor(options: UnifiedTaskServiceOptions = {}) {
    this.workflowDir = options.workflowDir || path.join(process.cwd(), '.workflow');
    this.gongfuDir = options.gongfuDir || path.join(process.cwd(), '.gongfu', 'task');
    this.autoSync = options.autoSync ?? true;
    
    // Register adapters
    AdapterFactory.register('gongfu', new GongfuAdapter());
    AdapterFactory.register('workflow', new WorkflowAdapter());
    
    // Ensure directories exist
    this.ensureDirectories();
  }
  
  /**
   * Get all tasks from both sources as unified tasks
   */
  async getAllTasks(): Promise<UnifiedTask[]> {
    const workflowTasks = await this.getWorkflowTasks();
    const gongfuTasks = await this.getGongfuTasks();
    
    // Merge and deduplicate by ID
    const taskMap = new Map<string, UnifiedTask>();
    
    // Add workflow tasks first
    workflowTasks.forEach(task => {
      taskMap.set(task.id, task);
    });
    
    // Add or update with gongfu tasks (gongfu takes precedence for conflicts)
    gongfuTasks.forEach(task => {
      const existing = taskMap.get(task.id);
      if (existing && existing.updated < task.updated) {
        taskMap.set(task.id, task);
      } else if (!existing) {
        taskMap.set(task.id, task);
      }
    });
    
    return Array.from(taskMap.values()).sort((a, b) => 
      b.updated.getTime() - a.updated.getTime()
    );
  }
  
  /**
   * Get tasks from workflow format
   */
  async getWorkflowTasks(): Promise<UnifiedTask[]> {
    const tasksFile = path.join(this.workflowDir, 'tasks.json');
    if (!await fs.exists(tasksFile)) {
      return [];
    }
    
    try {
      const data = await fs.readJson(tasksFile);
      const adapter = AdapterFactory.get<WorkflowAdapter>('workflow');
      return data.tasks?.map((task: Task) => adapter.toUnified(task)) || [];
    } catch (error) {
      console.error('Error reading workflow tasks:', error);
      return [];
    }
  }
  
  /**
   * Get tasks from gongfu format
   */
  async getGongfuTasks(): Promise<UnifiedTask[]> {
    if (!await fs.exists(this.gongfuDir)) {
      return [];
    }
    
    try {
      const files = await fs.readdir(this.gongfuDir);
      const yamlFiles = files.filter(f => f.endsWith('.yaml') || f.endsWith('.yml'));
      
      const tasks: UnifiedTask[] = [];
      const adapter = AdapterFactory.get<GongfuAdapter>('gongfu');
      
      for (const file of yamlFiles) {
        const content = await fs.readFile(path.join(this.gongfuDir, file), 'utf-8');
        const task = yaml.load(content) as GongfuTask;
        if (task && task.id) {
          tasks.push(adapter.toUnified(task));
        }
      }
      
      return tasks;
    } catch (error) {
      console.error('Error reading gongfu tasks:', error);
      return [];
    }
  }
  
  /**
   * Create or update a task in both formats
   */
  async saveTask(task: UnifiedTask): Promise<void> {
    // Save to workflow format
    await this.saveWorkflowTask(task);
    
    // Save to gongfu format if auto-sync is enabled
    if (this.autoSync) {
      await this.saveGongfuTask(task);
    }
  }
  
  /**
   * Save task in workflow format
   */
  private async saveWorkflowTask(task: UnifiedTask): Promise<void> {
    const tasksFile = path.join(this.workflowDir, 'tasks.json');
    const adapter = AdapterFactory.get<WorkflowAdapter>('workflow');
    
    // Read existing tasks
    let data = { tasks: [] as Task[] };
    if (await fs.exists(tasksFile)) {
      data = await fs.readJson(tasksFile);
    }
    
    // Convert to workflow format
    const workflowTask = adapter.fromUnified(task);
    
    // Update or add task
    const index = data.tasks.findIndex(t => t.id === workflowTask.id);
    if (index >= 0) {
      data.tasks[index] = workflowTask;
    } else {
      data.tasks.push(workflowTask);
    }
    
    // Save back
    await fs.writeJson(tasksFile, data, { spaces: 2 });
  }
  
  /**
   * Save task in gongfu format
   */
  private async saveGongfuTask(task: UnifiedTask): Promise<void> {
    const adapter = AdapterFactory.get<GongfuAdapter>('gongfu');
    const gongfuTask = adapter.fromUnified(task);
    
    const taskFile = path.join(this.gongfuDir, `${task.id}.yaml`);
    const content = yaml.dump(gongfuTask, {
      indent: 2,
      lineWidth: -1,
      noRefs: true,
      sortKeys: false,
    });
    
    await fs.writeFile(taskFile, content, 'utf-8');
  }
  
  /**
   * Delete a task from both formats
   */
  async deleteTask(taskId: string): Promise<void> {
    // Delete from workflow format
    await this.deleteWorkflowTask(taskId);
    
    // Delete from gongfu format
    await this.deleteGongfuTask(taskId);
  }
  
  /**
   * Delete task from workflow format
   */
  private async deleteWorkflowTask(taskId: string): Promise<void> {
    const tasksFile = path.join(this.workflowDir, 'tasks.json');
    if (!await fs.exists(tasksFile)) {
      return;
    }
    
    const data = await fs.readJson(tasksFile);
    data.tasks = data.tasks.filter((t: Task) => t.id !== taskId);
    await fs.writeJson(tasksFile, data, { spaces: 2 });
  }
  
  /**
   * Delete task from gongfu format
   */
  private async deleteGongfuTask(taskId: string): Promise<void> {
    const taskFile = path.join(this.gongfuDir, `${taskId}.yaml`);
    if (await fs.exists(taskFile)) {
      await fs.remove(taskFile);
    }
  }
  
  /**
   * Sync tasks between formats
   */
  async syncTasks(): Promise<{ synced: number; conflicts: UnifiedTask[] }> {
    const allTasks = await this.getAllTasks();
    let synced = 0;
    const conflicts: UnifiedTask[] = [];
    
    for (const task of allTasks) {
      try {
        await this.saveTask(task);
        synced++;
      } catch (error) {
        conflicts.push(task);
        console.error(`Error syncing task ${task.id}:`, error);
      }
    }
    
    return { synced, conflicts };
  }
  
  /**
   * Ensure required directories exist
   */
  private ensureDirectories(): void {
    fs.ensureDirSync(this.workflowDir);
    // DEPRECATED: .gongfu/task is no longer used, using .workflow as single source of truth
    // fs.ensureDirSync(this.gongfuDir);
  }
}